// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Panel.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * Panel component
 */

  import React, { Component } from 'react';
  import PropTypes from 'prop-types';
  import classnames from 'classnames';
  import Divider from 'share/components/controls/Izenda.Divider';
  import { observer } from 'mobx-react';
  import { isNil, isEmpty, isString } from 'lodash/fp';

  @observer
  class Panel extends Component {
    static propTypes = {
      resizable: PropTypes.bool,
      collapsible: PropTypes.bool,
      afterToggled: PropTypes.func,
      idPanelBody: PropTypes.bool,
      expanedOption: PropTypes.bool,
      rerenderOnExpaned: PropTypes.bool,
      header: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      footer: PropTypes.object,
      panelBodyClassName: PropTypes.string
    }

    static defaultProps = {
      resizable: false
    }

    state: {
      isExpanded: boolean
    }

    collapsible : boolean

    constructor(props: any) {
      super(props);
      const {
        header, initState, parent, collapsible, expanedOption
       } = props;

      this.collapsible = parent || collapsible ? true : false;
      const expandedStatus = !!header && !isNil(header.expanedOption) && !isEmpty(header.expanedOption);
      this.state = {
        isExpanded: initState || expanedOption || expandedStatus
      };
    }

    componentDidUpdate() {
      const { props, state: { isExpanded } } = this;
      props.afterToggled && props.afterToggled(isExpanded);
    }

    /**
     * Component will receive props
     */
    componentWillReceiveProps(nextProps: any) {
       const { header, expanedOption } = this.props;
       const nextPropsHeader = nextProps.header;

      // Toggle expand content when current & next props is difference
      if (
        nextPropsHeader &&
        !isNil(nextPropsHeader.expanedOption) &&
        nextPropsHeader.expanedOption !== header.expanedOption
      ) {
        expandedPanel.call(this, nextPropsHeader.expanedOption);
      }

      if (nextProps.expanedOption !== expanedOption) {
        expandedPanel.call(this, nextProps.expanedOption);
      }

      function expandedPanel(newState) {
        const expanded = !!newState;
        this.setState({
          isExpanded: expanded
        });
      }
    }

    /**
     * Show/hide content
     * @param {event} e
     */
    collapseToggle = () => {
      this.setState({
        isExpanded: !this.state.isExpanded
      });
    }

    /**
     * Render resizable panel body
     */
    renderResizableBody = (body: any, footer: any) : any => {

      const { defaultValue, minValue, maxValue, afterMove } = this.props;
      const config = { defaultValue, minValue, maxValue };

      return (
        <div className='filters-body-container'>
          <div style={{ height: defaultValue }}>
            {body}
            {footer}
          </div>
          <Divider
            afterMove={afterMove}
            config={config}
            orientation='vertical'
            nodeHandle='previousSibling'
          />
        </div>
      );
    }

    /**
     * Render component
     * @returns {react-element}
     */
    render() {
      const panel = this;
      const {
        state : { isExpanded },
        props : { className, style, panelKey, id }
      } = this;

      const classes = classnames('panel', className);

      return (
        <div
          className={classes}
          style={style}
          aria-expanded={isExpanded}
          key={panelKey}
          id={id}
        >
          {this.renderHeader()}
          {getBody.apply(this)}
        </div>
      );

      /**
       * Render panel-body
       * @returns {react-element}
       */
      function getBody() {
       if (panel.collapsible) {
          return panel.renderCollapseBody();
        } else {
          const footer = panel.renderFooter();
          return footer ? this.renderFixBody(panel.renderBody(), footer) : panel.renderBody();
        }
      }
    }

    /**
     * Render fixed panel body
     */
    renderFixBody(body : any, footer: any) : any {
      return (
        <div>
          {body}
          {footer}
        </div>
      );
    }

    /**
     * Handle click on title.
     */
    onClickTitleHandle = () => {
      const {
        props : {
          disabled,
          header,
          header : { rawTitle }
        }
      } = this;

      if (disabled) {
        return;
      }

      if (header.onClickTitle) {
        header.onClickTitle(rawTitle);
      }
    }

    /**
     * Rendfer header
     * @returns {react-element}
     */
    renderHeader = () => {
      const panel = this;
      const {
        state : {isExpanded },
        props : { header }
      } = this;

      if (!header) {
        return null;
      }

      const { style, className, icon, title } = header;

      const classes = classnames('panel-heading', className);
      return (
        <div className={classes} aria-expanded={isExpanded}>
          {getHeaderContent()}
        </div>
      );

      /**
       * Render header
       * @returns {react-component}
       */
      function getHeaderContent() {

        if (header.component) {
          return <header.component collapseToggle={panel.collapseToggle} isExpanded={isExpanded} {...header} />;
        } else {
          if (header.onClickTitle) {
            return (
              <strong style={style} className={className}>
                {icon ? (
                  <i className={icon} onClick={panel.collapsible ? panel.collapseToggle : null} />
                ) : null}
                <a onClick={panel.onClickTitleHandle}>{(header && title) || header}</a>
              </strong>
            );
          }
          return (
            <strong
              onClick={panel.collapsible ? panel.collapseToggle : null}
              style={style}
              className={className}
            >
              {icon ? <i className={icon} /> : null}
              {getHeader()}
            </strong>
          );
        }

        function getHeader() {
          return (isString(title) && title) || (isString(header) && header) || '';
        }
      }
    }

    /**
     * Render panel body: body & footer (for collapsible-component)
     * @returns {react-element}
     */
     renderCollapseBody = () => {
      const {
        state: { isExpanded },
        props : { disabled, renderCollapseContent, resizable }
      } = this;

      const classes = classnames('panel-collapse collapse', {
        in: isExpanded,
        'izenda-Panel_Disabled': disabled
      });

      let renderBody;

      if (renderCollapseContent) {
        renderBody = isExpanded ? getContent : null;
      } else {
        renderBody = getContent;
      }

      return (
        <div className={classes} disabled={disabled}>
          {renderBody && renderBody.apply(this)}
        </div>
      );

      function getContent() {
        if (resizable) {
          return this.renderResizableBody(this.renderBody(), this.renderFooter());
        }

        return (
          <div>
            {this.renderBody()}
            {this.renderFooter()}
          </div>
        );
      }
    }

    /**
     * Render panel body
     * @returns {type}
     */
    renderBody = () => {
      const {
        props : { disabled, panelBodyClassName, idPanelBody, children }
      } = this;
      const classes = classnames('panel-body', panelBodyClassName);
      return (
        <div id={idPanelBody} className={classes} disabled={disabled}>
          {children}
        </div>
      );
    };

    /**
     * Render footer
     * @returns {type}
     */
    renderFooter = () => {
      const { footer } = this.props;

      if (!footer) {
        return null;
      }
      const { className, title } = footer;
      const classes = classnames('panel-footer', className);
      return <div className={classes}>{getContent()}</div>;

      /**
       * Render footer content
       * @returns {react-element}
       */
      function getContent() {
        if (footer.component) {
          return React.createElement(footer.component, footer);
        } else {
          return footer && title;
        }
      }
    }
  }
  export default Panel;



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.Panel.js